<template>
  <div class="b-vehicle">
    <div v-if="showFlag" class="b-vehicle__flag">{{ flagContent }}</div>
    <div class="b-vehicle__tagged"></div>

    <div class="b-vehicle__body">
      <div class="b-vehicle__header mb-40">
        <div class="b-vehicle__title h4 extrabold">
          {{ item.manufacturer }} {{ item.short_model_name }}
        </div>
        <div class="b-vehicle__groups mb-10 semibold">
          <ul>
            <li>
              {{ modelNameComputed }}
            </li>
          </ul>
        </div>
        <div class="b-vehicle__details">
          <ul>
            <li>
              <div class="pair bold">
                <i class="icon-transmission"></i>
                <span>{{ transmissionLabel }}</span>
              </div>
            </li>
            <li>
              <div class="pair bold">
                <i class="icon-fuel"></i>
                {{ item.fuel_type }}
              </div>
            </li>
            <li v-if="showEmmission">
              <div class="pair bold">
                <i class="icon-emmission"></i>
                {{ item.co2 }} gr/km
              </div>
            </li>
            <li v-if="showConsumption">
              <div class="pair bold">
                <i class="icon-consumption"></i>
                {{ item.consumption }} lt/100km
              </div>
            </li>
          </ul>
        </div>
      </div>

      <SlickCarousel
        ref="slick"
        :key="'key-' + slickKey"
        class="b-vehicle__slick mb-30"
        :options="slickOptions">
        <div v-for="(image, index) in images" :key="index" class="item">
          <LazyImage
            v-if="image"
            :image="image"
            class="cursor-pointer"
            :title="dictionary.buttonVehicleViewMoreText"
            @click="onClickButton" />
        </div>
      </SlickCarousel>

      <div class="b-vehicle__footer mb-lg-30">
        <div class="b-vehicle__totals mb-20">
          <div
            class="b-vehicle__price-w-vat"
            :class="{ 'is-active': vatIncluded }">
            <div class="bold mb-5">
              <span class="animate">
                {{ dictionary.vehiclePricePrefix }}&nbsp;
                <em v-if="vatIncluded">
                  €{{ initialRentalPriceVatFormatted }}
                </em>
                <em v-else>€{{ initialRentalPriceFormatted }}</em>
                {{ dictionary.vehiclePriceSuffix }}
              </span>
            </div>

            <div class="form-check form-switch">
              <label class="form-check-label" :for="'vat' + item.vehicle_id">
                <span v-if="vatIncluded">
                  {{ dictionary.vehicleVatIncluded }}
                </span>
                <span v-else>{{ dictionary.vehicleVatExcluded }}</span>
              </label>
              <input
                :id="'vat' + item.vehicle_id"
                v-model="vatIncluded"
                class="form-check-input"
                type="checkbox"
                role="switch" />
            </div>
          </div>

          <button
            type="button"
            class="btn btn-primary btn-full-width"
            @click="onClickButton">
            {{ dictionary.buttonVehicleViewMoreText }}
          </button>
        </div>
      </div>

      <div class="d-none d-lg-flex justify-content-center">
        <div class="form-check">
          <input
            :id="'checkboxCompare' + item.vehicle_id"
            v-model="isChecked"
            class="form-check-input"
            type="checkbox"
            :disabled="disabledCompare" />
          <label
            class="form-check-label"
            :for="'checkboxCompare' + item.vehicle_id">
            {{ dictionary.compareLabel }}
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as lib from './../../lib/lib'
import { v4 as uuidv4 } from 'uuid'
import LazyImage from './common/LazyImage.vue'
import SlickCarousel from '../components/common/SlickCarousel.vue'
import mixinsLeaseItNow from './../mixins/leaseitnow-mixins'
import constants from './../../lib/constants'

export default {
  components: { LazyImage, SlickCarousel },
  mixins: [mixinsLeaseItNow],
  props: {
    item: {
      required: true,
      type: Object,
    },
  },
  emits: ['change-compare'],
  data() {
    return {
      vatIncluded: false,
      slickKey: uuidv4(),
      slickOptions: {
        infinite: true,
        autoplay: false,
        arrows: true,
        dots: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: false,
        variableWidth: false,
      },
    }
  },
  computed: {
    flagContent() {
      if (this.isElectric) {
        return this.dictionary.electrictFlag
      }
      if (this.isPlugin) {
        return this.dictionary.plugInFlag
      }
      return ''
    },
    showFlag() {
      return this.isElectric || this.isPlugin
    },
    isElectric() {
      return this.item.isElectric
    },
    isPlugin() {
      return this.item.hybridStatus === constants.leaseitnow.hybridStatus.plugin
    },
    showEmmission() {
      return lib.numberIsPositive(this.item.co2)
    },
    showConsumption() {
      return lib.numberIsPositive(this.item.consumption)
    },
    transmissionLabel() {
      let res
      const transmission = this.item.transmission
      const { manual, auto } = constants.leaseitnow.filters.transmissionsOptions

      switch (transmission) {
        case manual:
          res = this.dictionary.transmissionManual
          break
        case auto:
          res = this.dictionary.transmissionAutomatic
          break
        default:
          res = null
      }
      return res
    },

    modelNameComputed() {
      if (!this.item) {
        return null
      }
      /* eslint-disable camelcase */
      const { model_name, short_model_name } = this.item
      const modelName = model_name.replace(short_model_name, '')

      return `${modelName} ${this.item.model_key}`
    },

    isChecked: {
      get: function () {
        if (!this.recordsCompare.length) {
          return false
        }

        return this.recordsCompare
          .map((x) => x.vehicle_id)
          .some((x) => x === this.item.vehicle_id)
      },
      set: function (value) {
        value
          ? this.addRecordToCompare({ value: this.item })
          : this.removeRecordFromCompare({ value: this.item.vehicle_id })
      },
    },
    initialRentalPriceFormatted() {
      return Math.round(this.item.initial_rental_price)
    },
    initialRentalPriceVatFormatted() {
      return Math.round(this.item.initial_rental_price_vat)
    },
    images() {
      const images = this.item.gallery_images
      return images.length ? images.map((x) => ({ url: x, alt: '' })) : []
    },
    disabledCompare() {
      if (
        this.recordsCompare
          .map((i) => i.vehicle_id)
          .includes(this.item.vehicle_id)
      ) {
        return false
      }

      return this.recordsCompare.length >= this.configuration.compareMaxItems
    },
  },

  methods: {
    onClickButton() {
      /* eslint-disable camelcase */
      const { language } = this.$route.params
      const { slug, vehicle_id } = this.item
      this.$router.push({
        name: 'car',
        params: { language, slug, id: vehicle_id },
      })

      lib.scrollToTop()
    },
  },
}
</script>
